import React, { useEffect, useState } from "react";
import "../MATHSDOCTOR/styles/MD_ManageTopics.css"
import Loading from "../../Common/Loading";

interface TopicRow {
  Yr: number;
  Strand: string;
  StrandDesc: string;
  SubStrand: string;
  SubStrandDesc: string;
  Topic: string;
  TopicName: string;
  LessonCode: string;
  OldTopic?: number;
  TopicOrder?: number;
  NoOfQns: number;
  Remarks: string;
}

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

// Helper: get session login stored in sessionStorage/sessionStorage by your auth flow
function getSessionLogin(): string {
  // sessionStorage key: "L"  |  sessionStorage key: "UserName"
  return sessionStorage.getItem("UserName") || "";
}

const EC_ManageTopics: React.FC = () => {
  const [countries, setCountries] = useState<string[]>([]);
  const [country, setCountry] = useState("");
  const [rows, setRows] = useState<TopicRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<TopicRow | null>(null);
  const [insertMode, setInsertMode] = useState<"B" | "A" | null>(null);
  const [loading, setLoading] = useState(false);
  const [strands, setStrands] = useState<string[]>([]);
  const [subStrands, setSubStrands] = useState<string[]>([]);
  const [lessonSearch, setLessonSearch] = useState("");
  const [updateLessonSearch, setUpdateLessonSearch] = useState("");

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState<string | null>(null);

  // Both dropdowns (Add + Update) use the SAME blob folder list — same as VB
  const [blobLessons, setBlobLessons] = useState<string[]>([]);

  // Add Live Topic Form
  // strand/substrand here are loaded dynamically in VB (TPGM003); 
  // keeping them as simple dropdowns matching VB default values
  const [addForm, setAddForm] = useState({
    strand: "",
    substrand: "",
    year: "E00",
    lesson: ""
  });
  // Update Live Topic Form
  // sourceLesson = the blob folder chosen in the dropdown (ddlupdatedlsns)
  // destLessonCode = selected row's LessonCode (hidLessonCode) — fixed from previous bug
  const [updateForm, setUpdateForm] = useState({
    sourceLesson: ""   // chosen from dropdown; dest comes from selectedRow.LessonCode
  });

  // Manual Insert Form
  const [newForm, setNewForm] = useState({
    topicName: "",
    lessonCode: "",
  });

  // Edit Form (bottom panel)
  const [editForm, setEditForm] = useState({
    strandDesc: "",
    subStrandDesc: "",
    topicName: "",
    lessonCode: "",
    year: "",
  });



  const searchLesson = (value: string) => {
    setLessonSearch(value);

    const search = value.toUpperCase().trim();

    if (!search) {
      return;
    }

    const index = blobLessons.findIndex(
      lesson => lesson.toUpperCase() === search
    );

    if (index !== -1) {
      setAddForm(prev => ({
        ...prev,
        lesson: blobLessons[index]
      }));
    }
  };

  const searchUpdateLesson = (value: string) => {
    setUpdateLessonSearch(value);

    const search = value.toUpperCase().trim();

    if (!search) {
      return;
    }

    const index = blobLessons.findIndex(
      lesson => lesson.toUpperCase() === search
    );

    if (index !== -1) {
      setUpdateForm({
        sourceLesson: blobLessons[index]
      });
    }
  };

  const loadSubStrands = (strand: string) => {
    fetch(`${API_BASE}api/ec/substrands_EC/?strand=${strand}`)
      .then(r => r.json())
      .then(d => {
        setSubStrands(d.substrands || []);

        if (d.substrands?.length > 0) {
          setAddForm(prev => ({
            ...prev,
            substrand: d.substrands[0]
          }));
        }
      });
  };

  const loadStrands = () => {
    fetch(`${API_BASE}api/ec/strands_EC/`)
      .then(r => r.json())
      .then(d => {
        setStrands(d.strands || []);

        if (d.strands?.length > 0) {
          setAddForm(prev => ({
            ...prev,
            strand: d.strands[0]
          }));

          loadSubStrands(d.strands[0]);
        }
      });
  };

  useEffect(() => {
    document.body.style.backgroundColor = "#ffffff";
    document.body.style.fontFamily = "Arial";
    fetch(`${API_BASE}api/md/countries/`)
      .then(r => r.json())
      .then(d => setCountries(d.countries));
  }, []);

  const loadTopics = (c: string) => {
    setLoading(true);

    setCountry(c);
    setSelectedRow(null);
    setRows([]);
    setEditForm({
      strandDesc: "",
      subStrandDesc: "",
      topicName: "",
      lessonCode: "",
      year: ""
    });

    if (!c || c === "---Select Country---") {
      setLoading(false);
      return;
    }

    Promise.all([
      fetch(`${API_BASE}api/ec/topics/?country=${encodeURIComponent(c)}`)
        .then(async r => {
          const d = await r.json();

          if (!r.ok) {
            throw new Error(d.message || "Failed to load topics");
          }

          return d;
        }),
      fetch(`${API_BASE}api/ec/blob_folders/`).then(r => r.json())
    ])
      .then(([topics, blobs]) => {
        setRows(topics.rows);
        setBlobLessons(blobs.lessons || []);
      })
      .finally(() => setLoading(false));
  };

  const selectRow = (r: TopicRow) => {
    setSelectedRow(r);
    setInsertMode(null);
    setEditForm({
      strandDesc: r.StrandDesc,
      subStrandDesc: r.SubStrandDesc,
      topicName: r.TopicName,
      lessonCode: r.LessonCode,
      year: String(r.Yr),
    });
  };

  // -------------------------------------------------------------------------
  // Add Live Topic  (btnok_Click)
  // -------------------------------------------------------------------------
  const handleAddLiveTopic = () => {
    if (!addForm.lesson) { alert("Please select a lesson"); return; }

    setLoading(true);
    const f = new FormData();
    f.append("country", country);
    f.append("strand", addForm.strand);
    f.append("substrand", addForm.substrand);
    f.append("year", addForm.year);       // e.g. "K01"
    f.append("lesson", addForm.lesson);
    f.append("login", getSessionLogin());

    fetch(`${API_BASE}api/ec/add_live/`, { method: "POST", body: f })
      .then(r => r.json())
      .then(d => {
        setLoading(false);
        if (d.status === "ok") {
          setShowAddModal(false);
          setShowSuccessModal(`The lesson was successfully added as ${d.lessonName}`);
          loadTopics(country);
        } else {
          alert("Error: " + d.message);
        }
      })
      .catch(err => {
        setLoading(false);
        alert("Network error: " + err);
      });
  };

  // -------------------------------------------------------------------------
  // Update Live Topic  (btnupdateok_Click)
  // Key fix: destLessonCode = selectedRow.LessonCode (hidLessonCode in VB)
  //          sourceLesson   = ddlupdatedlsns.SelectedValue (the chosen blob folder)
  // -------------------------------------------------------------------------
  const handleUpdateLiveTopic = () => {

    if (!selectedRow) {
      alert("Please select a topic row first");
      return;
    }

    if (
      !updateForm.sourceLesson ||
      updateForm.sourceLesson === "--SELECT--"
    ) {
      alert("Please select the lesson that you want to update");
      return;
    }


    setLoading(true);
    const f = new FormData();
    f.append("country", country);
    f.append("destLessonCode", selectedRow.LessonCode);   // hidLessonCode
    f.append("sourceLesson", updateForm.sourceLesson);     // ddlupdatedlsns value
    f.append("login", getSessionLogin());

    fetch(`${API_BASE}api/ec/update_live/`, { method: "POST", body: f })
      .then(r => r.json())
      .then(d => {
        setLoading(false);
        if (d.status === "ok") {
          setShowUpdateModal(false);
          setShowSuccessModal(d.message);
          loadTopics(country);
        } else {
          // VB shows the message in the success panel even for "Please select" case
          setShowUpdateModal(false);
          setShowSuccessModal(d.message);
        }
      })
      .catch(err => {
        setLoading(false);
        alert("Network error: " + err);
      });
  };

  // -------------------------------------------------------------------------
  // Open Update Modal — mirrors VB openUpdatelesson():
  // Pre-selects the dropdown to match the currently selected row's LessonCode
  // -------------------------------------------------------------------------
  const openUpdateModal = () => {
    if (!country || country.startsWith("-")) {
      alert("Please select a country");
      return;
    }
    // Pre-select dropdown to match selected row's lesson code (VB behaviour)
    const preSelect = selectedRow ? selectedRow.LessonCode : "";
    const matchedLesson = blobLessons.find(l => l === preSelect) || "";
    setUpdateForm({ sourceLesson: matchedLesson });
    setShowUpdateModal(true);
  };

  // -------------------------------------------------------------------------
  // Manual Insert  (btnAddNewTopic_Click)
  // -------------------------------------------------------------------------
  const insertTopic = () => {
    if (!selectedRow || !insertMode) return;
    const topicOrder = insertMode === "B"
      ? (selectedRow.TopicOrder ?? 0) - 1
      : (selectedRow.TopicOrder ?? 0) + 1;

    const f = new FormData();
    f.append("country", country);
    f.append("year", String(selectedRow.Yr));
    f.append("strand", selectedRow.Strand);
    f.append("substrand", selectedRow.SubStrand);
    f.append("topicName", newForm.topicName);
    f.append("lessonCode", newForm.lessonCode);
    f.append("topicOrder", String(topicOrder));

    fetch(`${API_BASE}api/ec/add/`, { method: "POST", body: f })
      .then(() => {
        setInsertMode(null);
        setNewForm({ topicName: "", lessonCode: "" });
        loadTopics(country);
      });
  };

  // -------------------------------------------------------------------------
  // Save Edit  (btnSave_Click — PGM-460: only TopicName is editable)
  // -------------------------------------------------------------------------
  const saveEdit = () => {
    if (!selectedRow) return;
    const f = new FormData();
    f.append("country", country);
    f.append("yr", editForm.year);
    f.append("strand", selectedRow.Strand);
    f.append("substrand", selectedRow.SubStrand);
    f.append("topic", selectedRow.Topic);
    f.append("topicname", editForm.topicName);
    f.append("lessoncode", editForm.lessonCode);

    fetch(`${API_BASE}api/ec/save/`, {
      method: "POST",
      body: f
    })
      .then(async r => {
        const d = await r.json();

        if (!r.ok || d.status !== "ok") {
          throw new Error(d.message || "Failed to save topic");
        }

        return d;
      })
      .then(() => {
        loadTopics(country);
      })
      .catch(err => {
        alert(err.message);
      });
  };

  // -------------------------------------------------------------------------
  // Delete Topic  (btnDeleteTopic_Click)
  // -------------------------------------------------------------------------
  const deleteTopic = () => {
    if (!selectedRow || !window.confirm("Do you really want to delete the selected Topic?")) return;
    const f = new FormData();
    f.append("country", country);
    f.append("yr", String(selectedRow.Yr));
    f.append("strand", selectedRow.Strand);
    f.append("substrand", selectedRow.SubStrand);
    f.append("topic", selectedRow.Topic);

    fetch(`${API_BASE}api/ec/delete/`, { method: "POST", body: f })
      .then(() => loadTopics(country));
  };

  // Grid height adjustment
  useEffect(() => {
    const resizeGrid = () => {
      const grid = document.getElementById("lst_divScrollableTable");
      const header = document.querySelector(".md-top-table") as HTMLElement;
      if (!grid || !header) return;
      const h = window.innerHeight - header.offsetHeight - 170;
      grid.style.height = `${h}px`;
      grid.style.width = `${window.innerWidth - 30}px`;
    };
    window.addEventListener("resize", resizeGrid);
    resizeGrid();
    return () => window.removeEventListener("resize", resizeGrid);
  }, [country, selectedRow, insertMode]);

  // -------------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------------
  const headerStyle: React.CSSProperties = {
    position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#c7ddfd'
  };

  const overlayStyle: React.CSSProperties = {
    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999
  };

  const modalBoxStyle: React.CSSProperties = {
    backgroundColor: "#ffffff", borderRadius: "6px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.4)", minWidth: "450px",
    overflow: "hidden", fontFamily: "Arial, sans-serif"
  };

  const modalHeaderStyle: React.CSSProperties = {
    backgroundColor: "#e0e0e0", textAlign: "center", padding: "10px",
    fontWeight: "bold", fontSize: "14px", borderBottom: "1px solid #ccc", color: "#000"
  };

  const modalContentStyle: React.CSSProperties = {
    padding: "20px 30px", backgroundColor: "#fff"
  };

  const mutedInputStyle: React.CSSProperties = {
    backgroundColor: "#f0f0f0", color: "#6d6d6d",
    border: "1px solid #d0d0d0", fontWeight: "normal", width: "90%", padding: "2px"
  };

  const btnStyle: React.CSSProperties = {
    marginRight: "6px", cursor: "pointer"
  };

  return (
    <div>
      {/* Loading overlay */}
      {loading && <Loading />}
      {/* Top toolbar */}
      <table className="md-top-table">
        <tbody>
          <tr>
            <td colSpan={2} className="md-header">
              Manage Strand / SubStrand / Topic / Questions
            </td>
          </tr>
          <tr>
            <td style={{ fontSize: "16px" }}>
              Curriculum:&nbsp;
              <select
                onChange={e => loadTopics(e.target.value)}
                value={country}
                style={{ cursor: "pointer" }}
              >
                <option>---Select Country---</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </td>
            <td align="right">
              {/* Add Topic — enabled only when country selected (mirrors VB) */}
              <button
                id="buttonT"
                style={btnStyle}
                onClick={() => {
                  if (!country || country.startsWith("-")) {
                    alert("Please select a country");
                    return;
                  }

                  setAddForm({
                    strand: "",
                    substrand: "",
                    year: "E00",
                    lesson: ""
                  });
                  setLessonSearch("");
                  setSubStrands([]);
                  loadStrands();

                  setShowAddModal(true);
                }}
              >
                Add Topic
              </button>

              {/* Update — enabled only when country selected AND a row is selected */}
              <button
                id="buttonT"
                style={btnStyle}
                onClick={openUpdateModal}
                disabled={!selectedRow}
              >
                Update
              </button>

              {/* Insert Before / After — only visible when row selected (VB: display:none until row selected) */}
              {/* {selectedRow && (
                <>
                  <button
                    id="buttonT"
                    style={btnStyle}
                    onClick={() => { setInsertMode("B"); setNewForm({ topicName: "", lessonCode: "" }); }}
                  >
                    Insert Topic - Before
                  </button>
                  <button
                    id="buttonT"
                    style={btnStyle}
                    onClick={() => { setInsertMode("A"); setNewForm({ topicName: "", lessonCode: "" }); }}
                  >
                    Insert Topic - After
                  </button>
                </>
              )} */}

              <button
                id="buttonT"
                style={btnStyle}
                disabled={!selectedRow}
                onClick={deleteTopic}
              >
                Delete Topic
              </button>

              <button
                id="buttonT"
                style={btnStyle}
                disabled={!selectedRow}
                onClick={() => {
                  const params = new URLSearchParams({
                    Country: country,
                    Yr: String(selectedRow?.Yr),
                    Strand: selectedRow?.Strand || "",
                    SubStrand: selectedRow?.SubStrand || "",
                    Topic: selectedRow?.Topic || "",
                    LessonCode: selectedRow?.LessonCode || ""
                  });
                  window.open(`/WebAdmin/ENGLISHCHECK/EC_AddDeleteQn?${params.toString()}`, "_blank");
                }}
              >
                Questions
              </button>

              <button
                id="buttonT"
                style={btnStyle}
                onClick={() => window.location.href = "/SysAdmin"}
              >
                Back to Menu
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Grid */}
      {country && (
        <div className="md-grid-wrapper">
          <div style={{
            fontFamily: "Arial", fontWeight: "bold", fontSize: "10pt",
            padding: "8px 6px", borderBottom: "1px solid #80a0ff",
            backgroundColor: "#c7ddfd", color: "#2859ac", textAlign: "center", width: "100vw"
          }}>
            Topics For Country : {country}
          </div>
          <div className="clsGridContainer" id="lst_divScrollableTable">
            <table className="clsGridTable">
              <thead className="clsGridHeader">
                <tr style={{ backgroundColor: "pink" }}>
                  <th className="col-slno" style={headerStyle}>#</th>
                  <th className="col-strand" style={headerStyle}>Strand</th>
                  <th className="col-substrand" style={headerStyle}>SubStrand</th>
                  <th className="col-year" style={headerStyle}>Year</th>
                  <th className="col-topic" style={headerStyle}>Topic</th>
                  <th className="col-topicname" style={headerStyle}>TopicName</th>
                  <th className="col-lesson" style={headerStyle}>LessonCode</th>
                  <th className="col-lesson" style={headerStyle}>OldTopic</th>
                  <th className="col-lesson" style={headerStyle}>TopicOrder</th>
                  <th className="col-qns" style={headerStyle}>NoOfQns</th>
                  <th className="col-remarks" style={headerStyle}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const prev = i > 0 ? rows[i - 1] : null;
                  const isStrandMuted = prev && r.Strand === prev.Strand;
                  const isSubStrandMuted = prev && r.Strand === prev.Strand && r.SubStrand === prev.SubStrand;
                  const isYearMuted = prev && r.Yr === prev.Yr;
                  return (
                    <tr
                      key={`${r.LessonCode}-${r.Topic}-${r.TopicOrder}`}
                      className={`clsGridRow ${selectedRow === r ? "clsGridSelected" : ""
                        }`}
                      onClick={() => selectRow(r)}
                    >
                      <td className="col-slno">{i + 1}</td>
                      <td style={{ color: isStrandMuted ? "#cccccc" : "inherit" }}>
                        {r.Strand}) {r.StrandDesc}
                      </td>
                      <td style={{ color: isSubStrandMuted ? "#cccccc" : "inherit" }}>
                        {r.SubStrand}) {r.SubStrandDesc}
                      </td>
                      <td className="clsGridCenter" style={{ color: isYearMuted ? "#cccccc" : "inherit" }}>
                        {r.Yr}
                      </td>
                      <td>{r.Topic}</td>
                      <td>{r.TopicName}</td>
                      <td>{r.LessonCode}</td>
                      <td>{r.OldTopic}</td>
                      <td>{r.TopicOrder}</td>
                      <td className="clsGridCenter">{r.NoOfQns}</td>
                      <td style={{ color: "red" }}>{r.Remarks}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bottom panel — Edit selected topic (PGM-460: TopicName only editable) */}
      {selectedRow && !insertMode && (
        <div className="md-panel">
          <table width="100%">
            <tbody>
              <tr>
                <td width="10%">Strand:</td>
                <td width="35%">
                  <input className="TextStyle" value={editForm.strandDesc} readOnly disabled style={mutedInputStyle} />
                </td>
                <td width="10%">Sub Strand:</td>
                <td width="32%">
                  <input className="TextStyle" value={editForm.subStrandDesc} readOnly disabled style={mutedInputStyle} />
                </td>
                <td width="5%">Year:</td>
                <td width="8%">
                  <input className="TextStyle" value={editForm.year} readOnly disabled style={mutedInputStyle} />
                </td>
              </tr>
              <tr>
                <td>Topic Name:</td>
                <td>
                  <input
                    className="TextStyle"
                    value={editForm.topicName}
                    onChange={e => setEditForm({ ...editForm, topicName: e.target.value })}
                    style={{ width: "90%" }}
                  />
                </td>
                <td>Lesson Code:</td>
                <td>
                  <input className="TextStyle" value={editForm.lessonCode} readOnly disabled style={mutedInputStyle} />
                </td>
                <td colSpan={2}>
                  <button id="buttonT" onClick={saveEdit}>Save</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Manual Insert Panel */}
      {insertMode && selectedRow && (
        <div className="md-panel">
          <table width="100%">
            <tbody>
              <tr>
                <td width="15%">New Topic Name:</td>
                <td width="30%">
                  <input
                    className="TextStyle"
                    value={newForm.topicName}
                    onChange={e => setNewForm({ ...newForm, topicName: e.target.value })}
                  />
                </td>
                <td width="10%">Lesson Code:</td>
                <td width="15%">
                  <input
                    className="TextStyle"
                    value={newForm.lessonCode}
                    onChange={e => setNewForm({ ...newForm, lessonCode: e.target.value })}
                  />
                </td>
                <td width="25%">
                  Will be inserted {insertMode === "B" ? "before" : "after"} selected item
                </td>
                <td width="5%">
                  <button id="buttonT" onClick={insertTopic}>Save</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ---- MODAL: Add Live Topic ---- */}
      {showAddModal && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            <div style={modalHeaderStyle}>Adding of New ECLesson</div>
            <div style={modalContentStyle}>
              <table width="100%" cellPadding={4}>
                <tbody>
                  <tr>
                    <td align="right" style={{ fontSize: "14px" }}>Strand:</td>
                    <td>
                      <select
                        style={{ fontSize: "13px" }}
                        value={addForm.strand}
                        onChange={e => {
                          const value = e.target.value;

                          setAddForm(prev => ({
                            ...prev,
                            strand: value
                          }));

                          loadSubStrands(value);
                        }}
                      >
                        {strands.map(s => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td align="right" style={{ fontSize: "14px" }}>Substrand:</td>
                    <td>
                      <select
                        style={{ fontSize: "13px" }}
                        value={addForm.substrand}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            substrand: e.target.value
                          }))
                        }
                      >
                        {subStrands.map(s => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td align="right" style={{ fontSize: "14px" }}>Year:</td>
                    <td>
                      <select
                        style={{ fontSize: "13px" }}
                        value={addForm.year}
                        onChange={e => setAddForm({ ...addForm, year: e.target.value })}
                      >
                        {["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10"].map(x => (
                          <option key={x} value={`E${x}`}>{x}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td align="right" valign="top" style={{ fontSize: "14px", paddingTop: "8px" }}>
                      Choose EC lesson you want<br />to add:
                    </td>
                    <td valign="top" style={{ paddingTop: "8px" }}>
                      <div>
                        <input
                          type="text"
                          value={lessonSearch}
                          onChange={e => searchLesson(e.target.value)}
                          placeholder="Search lesson code..."
                          style={{
                            fontSize: "13px",
                            width: "180px",
                            marginBottom: "4px"
                          }}
                        />

                        <br />

                        <select
                          style={{ fontSize: "13px", minWidth: "150px" }}
                          value={addForm.lesson}
                          onChange={e =>
                            setAddForm(prev => ({
                              ...prev,
                              lesson: e.target.value
                            }))
                          }
                        >
                          <option value="">--SELECT--</option>

                          {blobLessons.map(l => (
                            <option key={l} value={l}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <button
                  onClick={handleAddLiveTopic}
                  style={{ marginRight: "10px", width: "80px", cursor: "pointer" }}
                >
                  OK
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);

                    setAddForm({
                      strand: "",
                      substrand: "",
                      year: "E00",
                      lesson: ""
                    });

                    setSubStrands([]);
                  }}
                  style={{ width: "80px", cursor: "pointer" }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- MODAL: Update Live Topic ---- */}
      {showUpdateModal && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            <div style={modalHeaderStyle}>Update EC Lesson</div>
            <div style={modalContentStyle}>
              <div style={{ textAlign: "center", color: "#444", marginBottom: "15px", fontSize: "14px" }}>
                Please select the EC lesson you want to update.
              </div>

              {/* Show which topic is being updated — mirrors VB linkedlsns span */}
              {selectedRow && (
                <div style={{ textAlign: "center", marginBottom: "10px", fontWeight: "bold", fontSize: "13px" }}>
                  Updating: {selectedRow.LessonCode}
                  {/* <Loading /> */}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
                <span style={{ fontWeight: "bold", marginRight: "10px", fontSize: "14px" }}>EC Lesson:</span>
          <div>
                  <input
                    type="text"
                    value={updateLessonSearch}
                    onChange={e => searchUpdateLesson(e.target.value)}
                    placeholder="Search lesson code..."
                    style={{
                      fontSize: "13px",
                      width: "220px",
                      display: "block",
                      marginBottom: "5px"
                    }}
                  />

                  <select
                    style={{ fontSize: "13px", minWidth: "220px" }}
                    value={updateForm.sourceLesson}
                    onChange={e =>
                      setUpdateForm({
                        sourceLesson: e.target.value
                      })
                    }
                  >
                    <option value="">--SELECT--</option>

                    {blobLessons.map(l => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <button
                  onClick={handleUpdateLiveTopic}
                  style={{ marginRight: "10px", width: "80px", cursor: "pointer" }}
                >
                  OK
                </button>
                <button
                  onClick={() => {
                    setShowUpdateModal(false);
                    setUpdateForm({
                      sourceLesson: ""
                    });
                  }}
                  style={{ width: "80px", cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- MODAL: Success ---- */}
      {showSuccessModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalBoxStyle, width: "420px" }}>
            <div style={{ ...modalHeaderStyle, borderBottom: "1px solid #c3e6cb" }}>Success</div>
            <div style={{ padding: "20px", textAlign: "center", fontSize: "16px" }}>
              {showSuccessModal}
            </div>
            <div style={{ textAlign: "center", paddingBottom: "16px" }}>
              <button
                onClick={() => setShowSuccessModal(null)}
                style={{ width: "80px", cursor: "pointer" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EC_ManageTopics;